import React, { useState, useEffect } from 'react';
import { banlistsApi } from '../services/api';
import { BannedCard } from '../types';

interface BanlistDisplayProps {
  rulesetId: string;
  onManageBanlist: () => void;
  refreshTrigger?: number; // Add refresh trigger prop
}

const BanlistDisplay: React.FC<BanlistDisplayProps> = ({
  rulesetId,
  onManageBanlist,
  refreshTrigger,
}) => {
  const [bannedCards, setBannedCards] = useState<BannedCard[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadBannedCards();
  }, [rulesetId, refreshTrigger]);

  const loadBannedCards = async () => {
    try {
      setLoading(true);
      const response = await banlistsApi.getAll(rulesetId);
      setBannedCards(response.data);
    } catch (error) {
      console.error('Failed to load banned cards:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveCard = async (bannedCardId: string) => {
    try {
      await banlistsApi.removeCard(rulesetId, bannedCardId);
      setBannedCards(prev => prev.filter(card => card.id !== bannedCardId));
    } catch (error) {
      console.error('Failed to remove card from banlist:', error);
      alert('Failed to remove card from banlist. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex justify-center items-center h-32">
          <div className="text-lg text-gray-600">Loading banned cards...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-lg font-semibold text-gray-800">
          Banned Cards ({bannedCards.length})
        </h3>
        <button
          onClick={onManageBanlist}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Manage Banlist
        </button>
      </div>

      {bannedCards.length === 0 ? (
        <div className="text-center py-8">
          <div className="text-gray-600 text-lg mb-2">No banned cards</div>
          <div className="text-gray-500 text-sm">
            Click "Manage Banlist" to add cards to the banlist
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {bannedCards.map((bannedCard) => (
            <div
              key={bannedCard.id}
              className="flex items-center justify-between p-3 border rounded-lg bg-red-50"
            >
              <div className="flex-1 min-w-0">
                <div className="font-medium text-gray-900 truncate">
                  {bannedCard.card_name}
                </div>
                {bannedCard.reason && (
                  <div className="text-sm text-gray-500 mt-1">
                    Reason: {bannedCard.reason}
                  </div>
                )}
                <div className="text-xs text-gray-400 mt-1">
                  Added: {new Date(bannedCard.created_at).toLocaleDateString()}
                </div>
              </div>
              <button
                onClick={() => handleRemoveCard(bannedCard.id)}
                className="px-3 py-1 bg-red-600 text-white rounded text-sm font-medium hover:bg-red-700 transition-colors"
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default BanlistDisplay;
